import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Collapse,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowRight as ArrowRightIcon,
  SwitchAccessShortcutAdd as AccessIcon,
} from '@mui/icons-material';

import { DEPARTMENTS } from '../data/department';
import {
  AddAccessibledepartments,
  facility_departments,
  LoadRoles,
  RemoveAccessibledepartments
} from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  AdminCard, AdminPageHead, ADMIN, BRAND, adminFieldSx, EmptyState,
} from './adminUi';

// ============================================================================
//  Department Access — assign departments to roles
// ============================================================================
//
//  THE PROBLEM WITH THE OLD SCREEN
//
//    It rendered every department, as a full card with a description, for
//    EVERY role, all expanded, all at once. With 35 departments and 19 roles
//    that is 665 cards on one page — so finding "Billing & Accounts" under
//    "Acc/ph" meant scrolling past several hundred controls you did not want,
//    and there was no way to narrow the department list at all. The one search
//    box filtered roles only.
//
//  WHAT CHANGED
//
//    1. TWO searches that compose. One narrows the roles, the other narrows
//       the departments. Filter to the role "Acc" and the department "billing"
//       and you are looking at a single checkbox.
//
//    2. Roles collapse. One open at a time, so the page is a short list of
//       roles rather than a wall of checkboxes. A collapsed role still shows
//       WHICH departments it has as chips — the thing you most often want to
//       know is answerable without opening anything.
//
//    3. Departments are grouped by category. That grouping was already in the
//       data (DEPARTMENTS is keyed by 'Clinical/Medical', 'Diagnostic &
//       Support', 'Administrative', 'Specialty Clinics') and the old screen
//       flattened it away with getAllDepartments(), turning four short lists
//       into one long one.
//
//    4. The rows are single-line instead of cards, so an open role is a screen
//       or two rather than a dozen.
//
//  All of the assignment logic below — the optimistic update, the rollback on
//  failure, the per-role in-flight guard — is unchanged. Only finding things
//  got fixed.

// The full catalogue. Same 35 for every install — the per-facility subset is
// fetched at runtime and applied below.
const CATALOGUE_CATEGORIES = Object.values(DEPARTMENTS).map(c => ({
  category: c.category,
  departments: c.departments,
}));
const ALL_DEPARTMENTS = CATALOGUE_CATEGORIES.flatMap(c => c.departments);

// ── Facility subscription filter ────────────────────────────────────────────
//
//  A facility only runs some of the 35. Offering all of them here invites an
//  administrator to grant a role access to a department this hospital does not
//  have, which then shows up as a dead entry on that user's dashboard.
//
//  `enabledIds === null` means the facility list is unavailable — either
//  migration 030 has not run or the request failed. In that case show
//  EVERYTHING. An admin screen that silently hides every department because a
//  lookup failed is a far worse failure than showing a few extra.
function narrowToFacility(categories, enabledIds) {
  if (!enabledIds) return categories;
  const allowed = new Set(enabledIds.map(Number));
  return categories
    .map(c => ({ ...c, departments: c.departments.filter(d => allowed.has(Number(d.id))) }))
    .filter(c => c.departments.length > 0);
}

// 'Radiology' exists twice in the source data (ids 16 and 20) with different
// descriptions. Rather than silently show two identical-looking rows, the
// duplicated ones are marked so whoever is assigning can tell them apart.
const DUPLICATE_NAMES = new Set(
  Object.entries(
    ALL_DEPARTMENTS.reduce((acc, d) => {
      acc[d.name] = (acc[d.name] || 0) + 1;
      return acc;
    }, {})
  ).filter(([, n]) => n > 1).map(([name]) => name)
);

const Access = () => {
  const [roleSearch, setRoleSearch] = useState('');
  const [deptSearch, setDeptSearch] = useState('');

  // Departments this facility subscribes to. null = not yet known or the
  // lookup failed; narrowToFacility() treats null as "show the whole
  // catalogue" rather than hiding everything.
  const [enabledDeptIds, setEnabledDeptIds] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(facility_departments);
        if (cancelled) return;
        // `configured: false` means migration 030 has not been applied — the
        // facility has made no choice yet, so every department still applies.
        if (data?.success && data.configured) setEnabledDeptIds(data.enabled || []);
      } catch (err) {
        console.error('facility departments load failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const [openRole, setOpenRole] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingRoles, setUpdatingRoles] = useState(new Set());

  useEffect(() => {
    loadRoles();
  }, []);

  // Load roles from API
  const loadRoles = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(LoadRoles);
      if (response.data.success) {
        setRoles(response.data.results);
      } else {
        throw new Error('Failed to load roles');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load roles';
      setError(errorMessage);

      await Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if user has access to department
  const checkDepartmentAccess = useCallback((user, deptId) => {
    if (!user.department) return false;

    // Handle different data formats
    if (Array.isArray(user.department)) {
      return user.department.includes(deptId);
    }

    // If it's a comma-separated string
    if (typeof user.department === 'string') {
      const departments = user.department.split(',').map(d => d.trim()).filter(d => d);
      return departments.includes(deptId.toString());
    }

    return false;
  }, []);

  // The ids a role currently holds, as an array of strings.
  const assignedIds = useCallback((user) => {
    if (!user.department) return [];
    if (Array.isArray(user.department)) return user.department.map(String);
    if (typeof user.department === 'string') {
      return user.department.split(',').map(d => d.trim()).filter(Boolean);
    }
    return [];
  }, []);

  // Update user departments in state
  const updateUserDepartments = useCallback((userRole, deptId, hasAccess) => {
    return roles.map(role => {
      if (role.role === userRole) {
        let updatedDepartments;

        if (hasAccess) {
          // Remove department
          if (Array.isArray(role.department)) {
            updatedDepartments = role.department.filter(d => d !== deptId);
          } else if (typeof role.department === 'string') {
            updatedDepartments = role.department
              .split(',')
              .map(d => d.trim())
              .filter(d => d && d !== deptId.toString())
              .join(',');
          } else {
            updatedDepartments = [];
          }
        } else {
          // Add department
          if (Array.isArray(role.department)) {
            updatedDepartments = [...(role.department || []), deptId];
          } else if (typeof role.department === 'string') {
            const existing = role.department ?
              role.department.split(',').map(d => d.trim()).filter(d => d) : [];
            updatedDepartments = [...existing, deptId.toString()].join(',');
          } else {
            updatedDepartments = [deptId];
          }
        }

        return { ...role, department: updatedDepartments };
      }
      return role;
    });
  }, [roles]);

  // Toggle department access with optimistic updates
  const toggleDepartmentAccess = async (userId, deptId, hasAccess, userRole) => {
    // Prevent multiple simultaneous updates for the same role
    if (updatingRoles.has(userRole)) {
      return;
    }

    // Add role to updating set
    setUpdatingRoles(prev => new Set([...prev, userRole]));

    // Store original state for potential rollback
    const originalRoles = [...roles];

    // Optimistic update - update UI immediately
    const updatedRoles = updateUserDepartments(userRole, deptId, hasAccess);
    setRoles(updatedRoles);

    try {
      const endpoint = hasAccess ? RemoveAccessibledepartments : AddAccessibledepartments;
      const response = await axios.post(endpoint, {
        userRole,
        deptId: deptId.toString()
      });

      if (response.data.success) {
        const departmentName = ALL_DEPARTMENTS.find(d => d.id === deptId)?.name || 'Department';

        const Toast = Swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });

        await Toast.fire({
          icon: 'success',
          title: `${departmentName} access ${hasAccess ? 'removed' : 'granted'}`
        });
      } else {
        throw new Error(response.data.message || 'API returned unsuccessful response');
      }
    } catch (error) {
      // Revert optimistic update on error
      setRoles(originalRoles);

      const errorMessage = error.response?.data?.message || 'Failed to update access';

      await Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'Retry',
        showCancelButton: true,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          toggleDepartmentAccess(userId, deptId, !hasAccess, userRole);
        }
      });
    } finally {
      setUpdatingRoles(prev => {
        const newSet = new Set(prev);
        newSet.delete(userRole);
        return newSet;
      });
    }
  };

  // ── Filtering ─────────────────────────────────────────────────────────────

  const filteredRoles = useMemo(() => {
    const q = roleSearch.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(r => String(r.role || '').toLowerCase().includes(q));
  }, [roles, roleSearch]);

  // Categories, with departments narrowed by the department filter. Categories
  // that end up empty are dropped, so a filter never leaves a bare heading
  // behind with nothing under it.
  // Narrowed to the departments this facility subscribes to BEFORE the search
  // filter, so the counts and the "of N departments" readout both describe the
  // facility rather than the catalogue.
  const facilityCategories = useMemo(
    () => narrowToFacility(CATALOGUE_CATEGORIES, enabledDeptIds),
    [enabledDeptIds]
  );

  const facilityDeptCount = useMemo(
    () => facilityCategories.reduce((n, c) => n + c.departments.length, 0),
    [facilityCategories]
  );

  const visibleCategories = useMemo(() => {
    const q = deptSearch.trim().toLowerCase();
    if (!q) return facilityCategories;
    return facilityCategories
      .map(c => ({
        category: c.category,
        // Matches the description too — "drug stock" finds Pharmacy, which is
        // how somebody who does not know the department's formal name will
        // actually look for it.
        departments: c.departments.filter(d =>
          `${d.name} ${d.description || ''}`.toLowerCase().includes(q)
        ),
      }))
      .filter(c => c.departments.length > 0);
  }, [deptSearch, facilityCategories]);

  const visibleDeptCount = useMemo(
    () => visibleCategories.reduce((n, c) => n + c.departments.length, 0),
    [visibleCategories]
  );

  // Open the role automatically when the search has narrowed to exactly one.
  // Typing a role name and then still having to click it is a pointless step.
  useEffect(() => {
    if (filteredRoles.length === 1) setOpenRole(filteredRoles[0].role);
  }, [filteredRoles]);

  const anyFilter = roleSearch.trim() || deptSearch.trim();
  const clearAll = () => { setRoleSearch(''); setDeptSearch(''); };

  return (
    <Box>
      <AdminPageHead
        icon={AccessIcon}
        title="Department Access"
        subtitle="Which departments each role is allowed to reach"
        actions={
          <Tooltip title="Reload roles" arrow>
            <span>
              <IconButton
                onClick={loadRoles}
                disabled={loading}
                size="small"
                aria-label="Reload roles"
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}
              >
                {loading
                  ? <CircularProgress size={15} sx={{ color: '#fff' }} />
                  : <RefreshIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      {/* ── The two searches ────────────────────────────────────────────────
          Side by side and equal weight, because they answer two different
          questions and you often need both at once. The old screen had one,
          for roles, and no way at all to narrow 35 departments. */}
      <AdminCard sx={{ mb: ADMIN.headGap }}>
        <Box sx={{
          display: 'grid', gap: 1.75,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
        }}>
          <TextField
            size="small"
            label="Find a role"
            placeholder="e.g. Acc, nurse, billing officer…"
            value={roleSearch}
            onChange={(e) => setRoleSearch(e.target.value)}
            sx={adminFieldSx}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: BRAND.inkFaint }} />
                </InputAdornment>
              ),
              endAdornment: roleSearch ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear role search" onClick={() => setRoleSearch('')}>
                    <CloseIcon sx={{ fontSize: 15, color: BRAND.inkFaint }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <TextField
            size="small"
            label="Find a department"
            placeholder="e.g. billing, pharmacy, drug stock…"
            value={deptSearch}
            onChange={(e) => setDeptSearch(e.target.value)}
            sx={adminFieldSx}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: BRAND.inkFaint }} />
                </InputAdornment>
              ),
              endAdornment: deptSearch ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear department search" onClick={() => setDeptSearch('')}>
                    <CloseIcon sx={{ fontSize: 15, color: BRAND.inkFaint }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted }}>
            <strong style={{ color: BRAND.ink }}>{filteredRoles.length}</strong> of {roles.length} role
            {roles.length === 1 ? '' : 's'}
            {'  ·  '}
            <strong style={{ color: BRAND.ink }}>{visibleDeptCount}</strong> of {facilityDeptCount} departments
          </Typography>
          {anyFilter && (
            <Button
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              onClick={clearAll}
              sx={{ textTransform: 'none', color: BRAND.inkMuted, fontWeight: 600, fontSize: 12, py: 0 }}
            >
              Clear both
            </Button>
          )}
        </Box>
      </AdminCard>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: ADMIN.radiusSm }}
          action={
            <IconButton aria-label="retry" color="inherit" size="small" onClick={loadRoles}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {/* ── Roles ──────────────────────────────────────────────────────────
          A collapsed list. One role open at a time; the rest stay one line
          each, so the page length is the number of roles rather than roles ×
          departments. */}
      {loading && roles.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredRoles.length === 0 ? (
        <AdminCard>
          <EmptyState
            icon={AccessIcon}
            title="No roles found"
            body={roleSearch
              ? `Nothing matches “${roleSearch.trim()}”.`
              : 'No roles have been created yet — add them under User Roles first.'}
          />
        </AdminCard>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.25 }}>
          {filteredRoles.map((user) => {
            const isUpdating = updatingRoles.has(user.role);
            const ids = assignedIds(user);
            const isOpen = openRole === user.role;
            const assignedNames = ids
              .map(id => ALL_DEPARTMENTS.find(d => d.id === id)?.name)
              .filter(Boolean);

            return (
              <Box
                key={user.id || user.role}
                sx={{
                  background: BRAND.surface,
                  border: `1px solid ${isOpen ? BRAND.navyLight : BRAND.line}`,
                  borderRadius: ADMIN.radius,
                  boxShadow: isOpen ? ADMIN.shadowLift : ADMIN.shadow,
                  overflow: 'hidden',
                  transition: 'border-color .18s ease, box-shadow .18s ease',
                }}
              >
                {/* ── Collapsed header. Clicking anywhere on it toggles. ── */}
                <Box
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={() => setOpenRole(isOpen ? null : user.role)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenRole(isOpen ? null : user.role); }
                  }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: { xs: 1.75, sm: 2.25 }, py: 1.5,
                    cursor: 'pointer',
                    background: isOpen ? BRAND.navySoft : 'transparent',
                    '&:hover': { background: BRAND.navySoft },
                    '&:focus-visible': { outline: `2px solid ${BRAND.navy}`, outlineOffset: -2 },
                  }}
                >
                  {isOpen
                    ? <ExpandMoreIcon sx={{ fontSize: 20, color: BRAND.navy, flexShrink: 0 }} />
                    : <ArrowRightIcon sx={{ fontSize: 20, color: BRAND.inkFaint, flexShrink: 0 }} />}

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: BRAND.ink }}>
                        {user.role}
                      </Typography>
                      {isUpdating && <CircularProgress size={13} sx={{ color: BRAND.navy }} />}
                    </Box>

                    {/* The assigned departments, right here on the closed row.
                        This is the change that removes most of the scrolling:
                        the usual question is "what can this role reach?", and
                        it no longer requires opening anything. */}
                    {assignedNames.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .5, mt: .6 }}>
                        {assignedNames.slice(0, 6).map(name => (
                          <Chip
                            key={name}
                            label={name}
                            size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: BRAND.greenSoft, color: BRAND.greenDark }}
                          />
                        ))}
                        {assignedNames.length > 6 && (
                          <Chip
                            label={`+${assignedNames.length - 6}`}
                            size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: BRAND.canvas, color: BRAND.inkMuted }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: 11.5, color: BRAND.inkFaint, mt: .35, fontStyle: 'italic' }}>
                        No departments assigned
                      </Typography>
                    )}
                  </Box>

                  <Chip
                    label={ids.length}
                    size="small"
                    sx={{
                      flexShrink: 0, height: 22, minWidth: 30, fontSize: 11.5, fontWeight: 800,
                      bgcolor: ids.length > 0 ? BRAND.navy : BRAND.canvas,
                      color:   ids.length > 0 ? '#fff' : BRAND.inkMuted,
                    }}
                  />
                </Box>

                {/* ── Expanded: departments, grouped by category ── */}
                <Collapse in={isOpen} unmountOnExit>
                  <Box sx={{ borderTop: `1px solid ${BRAND.line}`, px: { xs: 1.5, sm: 2 }, py: 1.75 }}>
                    {visibleCategories.length === 0 ? (
                      <Typography sx={{ fontSize: 13, color: BRAND.inkMuted, textAlign: 'center', py: 3 }}>
                        No department matches “{deptSearch.trim()}”.
                      </Typography>
                    ) : visibleCategories.map((cat) => (
                      <Box key={cat.category} sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}>
                        <Typography sx={{
                          fontSize: 10, fontWeight: 800, letterSpacing: '1px',
                          textTransform: 'uppercase', color: BRAND.inkFaint, mb: .85, px: .5,
                        }}>
                          {cat.category}
                        </Typography>

                        <Box sx={{
                          display: 'grid',
                          gap: .75,
                          gridTemplateColumns: {
                            xs: 'minmax(0, 1fr)',
                            md: 'repeat(2, minmax(0, 1fr))',
                            xl: 'repeat(3, minmax(0, 1fr))',
                          },
                        }}>
                          {cat.departments.map((dept) => {
                            const hasAccess = checkDepartmentAccess(user, dept.id);
                            return (
                              <Box
                                key={dept.id}
                                onClick={() => !isUpdating && toggleDepartmentAccess(user.id, dept.id, hasAccess, user.role)}
                                sx={{
                                  display: 'flex', alignItems: 'center', gap: .75,
                                  minWidth: 0,
                                  px: 1, py: .6,
                                  borderRadius: '8px',
                                  border: `1px solid ${hasAccess ? BRAND.green : BRAND.line}`,
                                  background: hasAccess ? BRAND.greenSoft : BRAND.surface,
                                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                                  opacity: isUpdating ? .6 : 1,
                                  transition: 'border-color .15s ease, background-color .15s ease',
                                  '&:hover': isUpdating ? {} : { borderColor: hasAccess ? BRAND.greenDark : BRAND.navyLight },
                                }}
                              >
                                <Checkbox
                                  checked={hasAccess}
                                  disabled={isUpdating}
                                  size="small"
                                  tabIndex={-1}
                                  // The row owns the click; without this the
                                  // checkbox fires it a second time and the
                                  // toggle appears to do nothing.
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => e.stopPropagation()}
                                  sx={{ p: .25, flexShrink: 0, pointerEvents: 'none', color: BRAND.inkFaint, '&.Mui-checked': { color: BRAND.green } }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: BRAND.ink, lineHeight: 1.3 }}>
                                    {dept.name}
                                    {/* Two departments are literally called
                                        'Radiology' in the source data. Marking
                                        them stops a blind guess between two
                                        rows that otherwise look identical. */}
                                    {DUPLICATE_NAMES.has(dept.name) && (
                                      <Typography component="span" sx={{ fontSize: 10, color: BRAND.warning, fontWeight: 700, ml: .5 }}>
                                        #{dept.id}
                                      </Typography>
                                    )}
                                  </Typography>
                                  <Typography noWrap sx={{ fontSize: 10.5, color: BRAND.inkMuted, lineHeight: 1.3 }}>
                                    {dept.description}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default Access
